import {
  cloneDeep,
  isObjectLike,
} from 'lodash';
import * as breakdownConstants from '@/constants/products/breakdown';
import * as commonConstants from '@/constants/common';
import {
  regNumberRegex,
} from '@/helpers/products/car';
import {
  nameRegex,
} from '@/helpers/common';
import {
  DATE_FORMAT,
  UtcDate,
} from '@/helpers/date';
import * as breakdownPayloadTemplates from '@/payloadTemplates/breakdown';
import { validate } from 'vee-validate';

const constants = Object.assign(
  {},
  commonConstants,
  breakdownConstants,
);

export const yourDetailsRules = (options) => (step = {}, data) => ({
  title: {
    in: {
      items: constants.TITLES,
    },
  },
  firstName: {
    name: [2, 50],
    regex: nameRegex,
  },
  surname: {
    name: [2, 50],
    regex: nameRegex,
  },
  dateOfBirth: {
    date_format: DATE_FORMAT,
    dateOfBirth: true,
  },
  email: {
    email: true,
    required: true,
  },
  postcode: {
    postcode: true,
  },
  houseNumberOrName: {
    typeof: 'string',
  },
  addressLine2: {
    typeof: 'string',
  },
  addressLine3: {
    typeof: 'string',
  },
  addressLine4: {
    typeof: 'string',
  },
  outbound: {
    required: true,
    typeof: {
      typecheck: 'boolean',
    },
  },
  userKeepSignIn: {
    required: !options.config.hiddenQuestions['user-keep-me-sign-in'],
    typeof: 'boolean',
  },
});

export const coverRules = () => (step = {}, data) => ({
  typeOfCover: {
    required: true,
    typeof: 'string',
    in: {
      items: constants.COVER_TYPE_PREVALIDATION,
    },
  },
  personalOrVehicleCover: {
    required: true,
    typeof: 'string',
    in: {
      items: constants.PERSONAL_OR_VEHICLE_COVER_PREVALIDATION,
    },
  },
  vehicleType: {
    required: true,
    typeof: 'string',
    in: {
      items: constants.VEHICLE_TYPES_PREVALIDATION,
    },
  },
  vehicleConfirm: {
    required: true,
    integer: true,
    digits: 8,
  },
  vehicleMake: {
    required: true,
    alphaNum: true,
    length: 2,
  },
  vehicleModel: {
    required: true,
    typeof: 'string',
  },
  engineSize: {
    required: true,
    typeof: 'string',
  },
  yearOfManufacture: {
    required: true,
    date_format: 'y',
  },
  vehicleRegistrationNumber: {
    regex: regNumberRegex,
    typeof: 'string',
  },
  vehicleMakeLabel: {
    required: true,
    typeof: 'string',
  },
  vehicleModelLabel: {
    required: true,
    typeof: 'string',
  },
  homeStart: {
    required: false,
    typeof: 'boolean',
  },
  altTravel: {
    required: false,
    typeof: 'boolean',
  },
  dateOfInsuranceStart: {
    date_format: DATE_FORMAT,
    after: {
      equal: true,
      compare: 'today',
    },
    before: {
      equal: true,
      compare: new UtcDate().addDays(constants.DATE_OF_INSURANCE_START_MAX_DAYS).format(),
    },
  },
});

export const createBreakdownRules = (data, config) => {
  const validationHelpers = {};

  validationHelpers.config = config;

  return {
    yourDetailsRules: yourDetailsRules(validationHelpers),
    coverRules: coverRules(validationHelpers),
  };
};

export const prevalidate = async (dataRaw, config) => {
  const data = cloneDeep(dataRaw);
  const rules = createBreakdownRules(data, config);

  await Promise.all(
    ['cover', 'yourDetails'].map(async stepId => {
      if (!data[stepId]) return;

      if (isObjectLike(data[stepId])) {
        const stepTemplate = breakdownPayloadTemplates[stepId];
        const stepFieldsSorted = Object.keys(stepTemplate).sort();
        const stepRules = rules[`${stepId}Rules`](data[stepId], data);

        const stepValidity = await Promise.all(stepFieldsSorted.map(field => {
          return validate(data[stepId][field], stepRules[field]);
        }));

        stepFieldsSorted.forEach((field, i) => {
          if (!stepValidity[i].valid) {
            data[stepId][field] = stepTemplate[field];
          }
        });
      }
    }),
  );

  return data;
};
